import type { NeedLine, ScannedExtra } from "@/api/transfer_check";
import { REMAINING_EPS, plural } from "@/lib/order-check";

/**
 * Гейт «Завершить» проверки перемещений.
 *
 * Факт сборки — правда, заказ подгоняется под него на «Завершить» (бэкенд
 * переписывает `Document_ЗаказНаПеремещение`: больше → увеличит строку, нет в
 * заказе → допишет, меньше → уменьшит, не собрано → уберёт). Поэтому перебор,
 * товар не из заказа и недобор НЕ блокируют — только подсвечиваются:
 * - `empty` — ничего не собрано, завершать нечего;
 * - `exact` — собрано ровно по заказу, заказ не изменится;
 * - `diff`  — есть расхождение, «Завершить» перепишет заказ.
 */
export type GateState = "empty" | "exact" | "diff";

export interface GateInfo {
  state: GateState;
  /** Сколько собрано СВЕРХ строк заказа (базовые единицы). */
  over: number;
  /** Строк заказа со сверх. */
  overLines: number;
  /** Сколько НЕ добрано до строк заказа, включая несобранные вовсе. */
  under: number;
  /** Строк заказа с недобором (включая несобранные вовсе). */
  underLines: number;
  /** Позиций (ном, хар), которых нет в заказе. */
  extras: number;
}

type GateInput = {
  items: unknown[];
  needs: Pick<NeedLine, "qty_needed" | "qty_scanned">[];
  extras: ScannedExtra[];
};

export function computeGate(detail: GateInput): GateInfo {
  let over = 0;
  let overLines = 0;
  let under = 0;
  let underLines = 0;
  for (const n of detail.needs) {
    const d = n.qty_scanned - n.qty_needed;
    if (d > REMAINING_EPS) {
      over += d;
      overLines += 1;
    } else if (d < -REMAINING_EPS) {
      under += -d;
      underLines += 1;
    }
  }
  const extras = detail.extras.length;
  let state: GateState = "exact";
  if (detail.items.length === 0) state = "empty";
  else if (over > REMAINING_EPS || under > REMAINING_EPS || extras > 0) state = "diff";
  return { state, over, overLines, under, underLines, extras };
}

/** Краткая сводка расхождения: «N сверх · M недобор · K новых» — числа
 *  ПОЗИЦИЙ, не штук: единицы у номенклатур разные, и сумма штук с коробами и
 *  килограммами ничего не значит. Точный список — в диалоге завершения. */
export function gateSummary(g: GateInfo): string {
  const parts: string[] = [];
  if (g.overLines > 0) parts.push(`${g.overLines} сверх`);
  if (g.underLines > 0) parts.push(`${g.underLines} недобор`);
  if (g.extras > 0)
    parts.push(`${g.extras} ${plural(g.extras, ["новая", "новые", "новых"])}`);
  return parts.join(" · ");
}

export type PosFilter = "remain" | "diff" | "done" | "all";

/** С какого фильтра открыть список позиций по тапу на сводку. */
export function filterForGate(state: GateState): PosFilter {
  return state === "diff" ? "diff" : "all";
}
